/**
 * 블로그 본문에 `BLOG_CONVERSION_CTA_MARKER`만 넣으면 같은 React CTA 카드가 마운트됩니다.
 * 제목·부제·버튼 기본값은 이 파일 한곳에서 관리하고, 특정 글만 바꿀 때만 `CTA_OVERRIDES_BY_SLUG`에 추가하면 됩니다.
 *
 * @see BlogConversionCtaSection
 */

import type { TBlogCtaAnalyticsName } from '@/app/_lib/analytics-events';

export const BLOG_CONVERSION_CTA_MARKER = '<!--blog-mount:blog-conversion-cta-->';

/** 제목/부제 기본값 — {@link IBlogConversionCtaMountConfig}나 컴포넌트 props에서 생략 시 사용 */
export const DEFAULT_BLOG_CONVERSION_CTA_TITLE =
  'Stop switching between tools. See everything in one timeline.';

export const DEFAULT_BLOG_CONVERSION_CTA_SUBTITLE =
  'Tindlo unifies your calendar, tasks, and docs in one timeline.\nStart free — and see your entire day in one place within minutes.';

export interface IBlogConversionCtaMountAction {
  label: string;
  href: string;
  ctaName: TBlogCtaAnalyticsName;
  visual: 'primary' | 'secondary' | 'tertiary';
}

export interface IBlogConversionCtaMountConfig {
  /** 생략 시 {@link DEFAULT_BLOG_CONVERSION_CTA_TITLE} */
  title?: string;
  /** 제목 바로 아래 보조 한 줄 (선택) */
  lead?: string;
  /** 생략 시 {@link DEFAULT_BLOG_CONVERSION_CTA_SUBTITLE} */
  subtitle?: string;
  actions: readonly IBlogConversionCtaMountAction[];
}

/** 모든 글 공통 버튼 행 — 바꾸려면 여기만 수정하거나 슬러그 오버라이드 사용 */
export const DEFAULT_BLOG_CONVERSION_CTA_ACTIONS: readonly IBlogConversionCtaMountAction[] = [
  {
    label: 'Start Free Trial',
    href: 'https://www.tindlo.com',
    ctaName: 'start_free_trial',
    visual: 'primary',
  },
  {
    label: 'Compare Pricing',
    href: 'https://www.tindlo.com/pricing',
    ctaName: 'compare_pricing',
    visual: 'secondary',
  },
  {
    label: 'Email us for a demo',
    href: 'mailto:hello@tindlo.com',
    ctaName: 'email_demo',
    visual: 'tertiary',
  },
];

/** 특정 슬러그만 문구/버튼을 덮어쓸 때만 추가 (대부분 비워 둠) */
const CTA_OVERRIDES_BY_SLUG: Partial<
  Record<string, Partial<Pick<IBlogConversionCtaMountConfig, 'title' | 'lead' | 'subtitle' | 'actions'>>>
> = {
  'best-project-management-tools-small-teams-2026': {
    lead: 'Join teams already simplifying their workflow with Tindlo.',
    subtitle:
      'Tindlo unifies your calendar, tasks, and docs in one timeline.\nStart free — and see your entire day in one place within minutes.',
  },
  'notion-alternatives-project-management': {
    title: 'Stop Managing Docs. Start Shipping Work.',
    lead: 'Join teams moving from planning → execution.',
    subtitle:
      'Notion is great for docs — but execution needs a different system.\nTindlo connects tasks, time, and context so your team actually ships.',
  },
  'team-collaboration-complete-guide': {
    title: "Ready to unify your team's workflow?",
    subtitle: 'Start using Tindlo today and experience better collaboration.',
  },
  // 예: 'some-post-slug': { title: 'Custom headline', actions: [...] },
};

/**
 * 마커가 있는 모든 글에서 호출 — 기본 설정 + (있으면) 슬러그별 오버라이드.
 * 새 글마다 파일을 늘리지 않아도 됩니다.
 */
export function resolveBlogConversionCtaConfig(slug: string): IBlogConversionCtaMountConfig {
  const o = CTA_OVERRIDES_BY_SLUG[slug];
  return {
    title: o?.title,
    lead: o?.lead,
    subtitle: o?.subtitle,
    actions: o?.actions ?? DEFAULT_BLOG_CONVERSION_CTA_ACTIONS,
  };
}
